import { cn } from "@/utils/cn";
import { TruncatedText } from "@/components/common/truncated-text";
import { VoiceTemplatePlayIcon } from "./assets/voice-template-play-icon";
import { StopIcon } from "./assets/stop-icon";
import { audioControllerHooks, AudioPlayState } from "@/manager/manager/audio-controller-manager";
import { Tooltip } from "../ui/modal";
import { useTranslation } from "react-i18next";
import voiceBg from "@/assets/agent/voice-bg@3x.webp";
import { VoiceImage } from "./assets/voice-image";
import { Plus, PlusIcon } from "lucide-react";

const CircularProgress: React.FC<{ size?: number; stroke?: number; progress: number; className?: string }> = ({ size = 24, stroke = 3, progress, className = "" }) => {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(1, progress || 0));
  const offset = circumference * (1 - clamped);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={className}>
      <circle cx={size / 2} cy={size / 2} r={radius} stroke="#E4E7EC" strokeWidth={stroke} fill="none" />
      <circle cx={size / 2} cy={size / 2} r={radius} stroke="#101828" strokeWidth={stroke} fill="none" strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" transform={`rotate(-90 ${size / 2} ${size / 2})`} />
    </svg>
  );
};

/** 废弃 */
export const VoiceTemplateCapsuleItem: React.FC<{ selected?: boolean; label: string; audioUrl: string; onTap?: () => void }> = ({ selected = false, label, audioUrl, onTap }) => {
  const { playState, duration, currentTime, play } = audioControllerHooks.useController();
  const progress = duration > 0 ? currentTime / duration : 0;
  const isPlaying = playState === AudioPlayState.PLAYING;
  const handleClick = async () => {
    if (audioUrl) {
      await play(audioUrl);
    }
    onTap?.();
  };
  return (
    <button
      className={cn(
        'flex items-center gap-2 rounded-[10px] px-2.5 py-2 cursor-pointer border',
        selected ? 'border-black' : 'border-transparent',
        'bg-[rgba(242,244,247,1)]'
      )}
      onClick={handleClick}
      aria-label={isPlaying ? `暂停 ${label}` : `播放 ${label}`}
    >
      <div className="relative w-6 h-6">
        <CircularProgress progress={progress} className="absolute inset-0" />
        <div className="absolute inset-0 flex items-center justify-center">
          {isPlaying ? (
            <div className="w-5 h-5 rounded-full bg-[#101828] flex items-center justify-center">
              <StopIcon className="w-2.5 h-3 text-white" />
            </div>
          ) : (
            <VoiceTemplatePlayIcon className="w-5 h-5" />
          )}
        </div>
      </div>
      <TruncatedText as="p" text={label} className="text-[13px] font-medium text-black truncate max-w-[120px]" />
    </button>
  );
};

/**
 * 声音模板item
 * @param
 * @returns 
 */
export const VoiceTemplateCapsuleItemNew: React.FC<{
  label: string;
  audioUrl: string;
  showCreate?: boolean;
  onClick?: () => void
}> = ({ label, audioUrl, showCreate, onClick }) => {
  const { t } = useTranslation();
  const { playState, duration, currentTime, play } = audioControllerHooks.useController();
  const progress = duration > 0 ? currentTime / duration : 0;
  const isPlaying = playState === AudioPlayState.PLAYING;
  const handlePlay = async () => {
    if (audioUrl) {
      await play(audioUrl);
    }
  };
  return (
    <button
      className={cn(
        'relative flex flex-col justify-center items-center gap-1 rounded-[10px] px-2.5 py-2 cursor-pointer min-w-[52px] max-w-[52px] h-[70px]',
        showCreate && "bg-[#f3f4f6] hover:scale-110 transition-all",
      )}
      onClick={onClick}
      style={{
        backgroundImage: showCreate ? 'none' : `url(${voiceBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <Tooltip
        side='top'
        align='center'
        offset={23}
        trigger={
          <div
            className="relative w-7 h-7 flex-shrink-0 hover:scale-105 transition-all"
            onClick={(e) => {
              e.stopPropagation();
              handlePlay();
            }}>
            {/* 播放/暂停 */}
            {!showCreate && (
              <div className="absolute inset-0 flex items-center justify-center">
                {isPlaying ? (
                  <div className="w-6 h-6 rounded-full bg-[#101828] flex items-center justify-center">
                    <StopIcon className="w-3 h-3 text-white" />
                  </div>
                ) : (
                  <VoiceTemplatePlayIcon className="w-6 h-6" />
                )}
              </div>
            )}
            {/* 创建 */}
            {showCreate && (
              <div className="absolute pt-1 inset-0 flex items-center justify-center" onClick={onClick}>
                <Plus className="size-5 text-[rgba(152,162,179,1)]" />
              </div>
            )}
          </div>
        }
        content={
          !showCreate ? (
            <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px] z-20'>
              <span className='text-[rgba(255,255,255,1)] text-sm'>
                {!isPlaying ? t('agent_creation.input.play_voice') : t('agent_creation.input.stop_voice')}
              </span>
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
            </div>
          ) : null
        }
      />
      <p className="text-[13px] font-medium text-[rgba(16,24,40,1)] truncate max-w-[45px] text-center">
        {label}
      </p>
    </button>
  );
};